/* eslint-disable @typescript-eslint/no-unused-vars */
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import React, { useEffect, useRef } from "react";
import { CarouselProps } from "react-material-ui-carousel/dist/components/types";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import type { SwiperOptions } from "swiper/types";
import { CustomSwiperContainer } from "./styles";
import { isValidElement } from "react";
import { usePopupsState } from "hooks/usePopupsState";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import { isTouchInteraction } from "utils/isTouchInteraction";

const swiperParams: SwiperOptions = {
	autoplay: {
		enabled: true,
		delay: 7000,
		pauseOnMouseEnter: true,
		disableOnInteraction: false
	},
	modules: [Autoplay, Navigation, Pagination],
	navigation: true,
	pagination: {
		clickable: true,
		dynamicBullets: true,
		dynamicMainBullets: 4
	},
	//loop: false,
	rewind: true,
	slidesPerView: 1,
	spaceBetween: 30,
	slidesOffsetAfter: 0
};

const DailyDealsCarousel = (props: CarouselProps) => {
	const swiperRef = useRef<SwiperRef | null>(null);
	const { isAnyPopupOpen } = usePopupsState();
	const { userTracker } = useWebStoreContext();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	const setAutoplay = (enabled: boolean) => {
		const swiper = swiperRef.current?.swiper;

		if (!swiper || swiper.destroyed) {
			return;
		}

		if (enabled) {
			if (!swiper.autoplay.running) {
				swiper.autoplay.paused = false;
				swiper.autoplay.start();
			}
			return;
		}

		if (swiper.autoplay.running) {
			swiper.autoplay.stop();
		}
		swiper.autoplay.paused = false;
	};

	const reportRenderSlide = (n: number) => {
		const slides = Array.isArray(props.children) ? props.children : [];

		if (!slides[n]) {
			return;
		}
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "lp_carousel_view",
			event_type: "lp_bundle",
			action: "lp_carousel_view",
			source: "loyalty_shop",
			offer_id: slides?.[n]?.props?.data?.id,
			value_int: Number(slides?.[n]?.props?.data?.loyaltyPointPrice),
			value_int2: Number(slides?.[n]?.props?.data?.crossOutValue),
			value_int3: userTracker?.loyaltyPoints
		});
	};

	useEffect(() => {
		setAutoplay(!isAnyPopupOpen);
	}, [isAnyPopupOpen, props?.children]);

	useEffect(() => {
		reportRenderSlide(0);
	}, [Array.isArray(props.children) ? props.children.length : 0]);

	return (
		<CustomSwiperContainer>
			<Swiper
				id="carousel-swiper"
				ref={swiperRef}
				{...swiperParams}
				onTouchStart={(_, event) => {
					if (isTouchInteraction(event)) {
						setAutoplay(false);
					}
				}}
				onTouchEnd={(_, event) => {
					if (isTouchInteraction(event)) {
						setAutoplay(!isAnyPopupOpen);
					}
				}}
				onSlideChangeTransitionStart={(swiper) => {
					reportRenderSlide(swiper.realIndex);
				}}
			>
				{React.Children.map(props.children, (child, i) =>
					isValidElement(child) ? (
						<SwiperSlide key={child.key}>{child}</SwiperSlide>
					) : null
				)}
			</Swiper>
		</CustomSwiperContainer>
	);
};

export default DailyDealsCarousel;
